<template>
  <div class="users">
    <div class="search-box">
      <input v-model="searchQuery" type="search" :placeholder="$t('labels.search').toUpperCase()">
      <span><icon-search /></span>
    </div>
    <h1>{{ $t('nav.users') }}</h1>
    <data-table :headings="headings" :rows="users" :lazy-load="true" :search-query="searchQuery">
      <template v-slot:roleSlot="slotProps">
        <template v-if="slotProps.row.editing">
          <multiselect
            :class="['role-select', slotProps.row.newRole ? 'selected ' + slotProps.row.newRole.name : '']"
            v-model="slotProps.row.newRole"
            :options="computedRoles"
            track-by="name"
            label="label"
            :searchable="false"
            :show-labels="false"
            :allow-empty="false"
          />
          <a href="#" @click.prevent="saveRole(slotProps.row)">{{ $t('buttons.save') }}</a>
        </template>
        <template v-else>
          <span v-if="slotProps.row.role === 'pending'" class="role pending">{{ $t('users.pending') }}</span>
          <span v-else :class="['role', slotProps.row.role.name]">{{ roles[slotProps.row.status].label }}</span>
          <a v-if="isAdmin" href="#" @click.prevent="editRole(slotProps.row)">
            <span v-if="slotProps.row.role == 'pending'">{{ $t('users.assignRole') }}</span>
            <span v-else>{{ $t('users.editRole') }}</span>
          </a>
        </template>
      </template>
    </data-table>
  </div>
</template>

<script>
import DataTable from '@/components/DataTable.vue'
import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.min.css'
import IconSearch from '@/assets/images/icons/search_icon.svg'
import { GET_USERS, SAVE_USER } from '@/store/actions.type'

export default {
  name: 'users',
  components: {
    DataTable,
    Multiselect,
    IconSearch
  },
  data() {
    return {
      searchQuery: '',
      users: []
    }
  },
  computed: {
    headings() {
      return [
        {
          label: this.$t('login.email'),
          key: 'email',
          defaultSort: true
        },
        {
          label: this.$t('users.firstName'),
          key: 'firstName'
        },
        {
          label: this.$t('users.lastName'),
          key: 'lastName'
        },
        {
          label: this.$t('users.role'),
          key: 'role'
        }
      ]
    },
    roles() {
      return {
        3: {
          id: 3,
          name: 'admin',
          label: this.$t('users.admin')
        },
        2: {
          id: 2,
          name: 'editor',
          label: this.$t('users.editor')
        },
        1: {
          id: 1,
          name: 'viewer',
          label: this.$t('users.viewer')
        }
      }
    },
    isAdmin() {
      return this.$store.getters.isAdmin
    },
    computedRoles() {
      return [3, 2, 1].map(a => this.roles[a])
    },
  },
  methods: {
    editRole(row) {
      this.$set(row, 'newRole', this.roles[row.role.id])
      this.$set(row, 'editing', true)
    },
    saveRole(row) {
      if (row.newRole) {
        const newUser = Object.assign({}, row, {
          status: row.newRole.id
        })
        this.$store.dispatch(SAVE_USER, newUser)
        this.$set(row, 'role', row.newRole)
        this.$set(row, 'status', row.newRole.id)
      }
      this.$set(row, 'editing', false)
    }
  },
  created() {
    const loader = this.$loading.show()
    this.$store.dispatch(GET_USERS).finally(() => {
      this.users = this.$store.getters.users.map(user => {
        return Object.assign({}, user, { role: this.roles[user.status] })
      })
      loader.hide()
    })
  }
}
</script>

<style lang="scss" scoped>
.search-box {
  display: flex;
  align-items: flex-end;
  float: right;
  width: 100%;
  max-width: 400px;

  span {
    display: inline-block;
    margin-bottom: em(7);
    max-width: 20px;
    max-height: 20px;

    svg {
      fill: currentColor;
    }
  }
}
%role {
  text-transform: capitalize;
  border-radius: 4px;
  padding: em(9) em(20);
}
%admin {
  color: #4399B0;
  background-color: #CBEEF3;
}
%editor {
  color: #898989;
  background-color: #ddd;
}
%viewer {
  color: #ADADAD;
  background-color: #fff;
  border: 1px solid #898989;
}
%pending {
  color: #EB3323;
  text-transform: uppercase;
  border-radius: 0;
  padding: 0;
}
.data-table {
  /deep/ {
    .role {
      @extend %role;

      &.admin {
        @extend %admin;
      }
      &.editor {
        @extend %editor;
      }
      &.viewer {
        @extend %viewer;
      }
      &.pending {
        @extend %pending;
      }

      & + a {
        margin: 20px;
      }
    }
  }
}
.role-select {
  display: inline-block;
  margin-right: 20px;
  min-width: 140px;
  width: auto;
  vertical-align: middle;

  /deep/ {
    .multiselect__tags {
      border: none;
      border-bottom: 1px solid #0B0F4D;
      padding-left: 0;
    }
    .multiselect__single {
      @extend %role;
      width: auto;
    }
  }
  &.admin /deep/ .multiselect__single {
    @extend %admin;
  }
  &.editor /deep/ .multiselect__single {
    @extend %editor;
  }
  &.viewer /deep/ .multiselect__single {
    @extend %viewer;
  }
  &.pending /deep/ .multiselect__single {
    @extend %pending;
  }
  &.selected /deep/ {
    .multiselect__tags {
      padding-top: 0;
    }
  }
}
</style>
